/**
 * CSRF token fetcher with single-in-flight dedupe.
 *
 * Per saas_admin_backend_contract.md §2.7.5, the SPA reads the `journix_csrf`
 * cookie (not HttpOnly) and echoes it in both `X-CSRF-Token` and the
 * `csrfToken` body field. We attempt a cookie read first; if it's missing, we
 * fall back to `GET /api/auth/csrf` which sets the cookie and returns the
 * token.
 *
 * Concurrent callers share one fetch. Transport is axios (`withCredentials`
 * carries cookies, matching the prior `credentials:'include'`); the
 * cookie-first / fallback behavior is unchanged. This uses the default axios
 * (not client.ts's shared instance) to keep this module dependency-light and
 * avoid an import cycle with client.ts.
 */
import axios from 'axios';
import { ApiError } from './errors';
import { generateRequestId } from '@/utils/uuid';

let inFlight: Promise<string> | null = null;

export function __resetCsrfInFlight(): void {
  inFlight = null;
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${escaped}=([^;]+)`));
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

export function fetchCsrfToken(baseUrl: string): Promise<string> {
  const cookie = readCookie('journix_csrf');
  if (cookie) return Promise.resolve(cookie);
  if (inFlight) return inFlight;
  inFlight = doFetchCsrf(baseUrl).finally(() => {
    inFlight = null;
  });
  return inFlight;
}

interface CsrfResponse {
  csrfToken?: string;
}

async function doFetchCsrf(baseUrl: string): Promise<string> {
  const requestId = generateRequestId();
  let res;
  try {
    res = await axios.request<CsrfResponse>({
      url: `${baseUrl}/api/auth/csrf`,
      method: 'GET',
      withCredentials: true,
      // Resolve (don't throw) on any status so the !ok check below owns the
      // mapping to ApiError, matching the prior fetch path.
      validateStatus: () => true,
      headers: {
        Accept: 'application/json',
        'X-Request-ID': requestId,
      },
    });
  } catch {
    throw new ApiError('NETWORK_ERROR', 0, { requestId });
  }
  if (res.status < 200 || res.status >= 300) {
    throw new ApiError('UNKNOWN', res.status, { requestId });
  }
  const body = res.data;
  if (!body?.csrfToken) {
    throw new ApiError('UNKNOWN', res.status, {
      requestId,
      details: { message: 'CSRF response missing csrfToken' },
    });
  }
  return body.csrfToken;
}
